<!-- eslint-disable max-len -->

<template>
  <article v-if="isGenericFlight && isExperimentEnabled" class="green-guide">
    <div class="content">
      <EBadge class="sustainability-label" @click.prevent>
        <EIcon icon="green-leaf" size="s" class="sustainability-label__icon" />
        <span> Green guide </span>
      </EBadge>
      <EHeading as="h4">
        How To Fly More Sustainably
      </EHeading>
      <div class="intro" @click="toggleReadMore">
        <div>{{ showMore ? 'Hide Green Tips for Flights' : 'Show Green Tips for Flights' }}</div>
        <div class="readMoreButton">
          <EButton
            :icon="toggleIcon"
            :class="{'read-more-btn-active': showMore}"
            variant="bare"
            icon-position="end"
            size="s"
          />
        </div>
      </div>
      <div class="guide-text">
        <transition name="toggle" mode="in-out">
          <div v-if="showMore" class="more-content">
            <ol>
              <li>
                <!-- Choose Direct Flights: Non-stop flights help reduce your carbon footprint by using less fuel and emitting fewer emissions. -->
                Consider taking a train or bus instead of a flight if possible.
              </li>
              <li>
                <!-- Pack Lightly: The heavier the plane, the more fuel it burns. By packing only what you need, you can help reduce the weight on the plane, and ultimately, reduce its emissions. -->
                Choose a non-stop flight, as takeoffs and landings create the most emissions.
              </li>
              <li>
                <!-- Fly Economy: First-class seats take up more space, which means fewer passengers can fit on the plane. By flying economy, you're helping to reduce the carbon footprint per passenger. -->
                Offset your flight's carbon emissions by donating to a reputable carbon offset program.
              </li>
              <li>
                <!-- Offset Your Carbon: Consider buying carbon offsets to balance out the emissions from your flight. Many airlines and third-party providers offer carbon offset programs that support clean energy and conservation projects. -->
                Choose economy class when flying, as the carbon footprint per passenger decreases with larger groups.
              </li>
              <li>
                <!-- Choose Eco-Friendly Airlines: Some airlines are taking steps to reduce their environmental impact by investing in more fuel-efficient planes, using sustainable biofuels, and recycling waste. Do some research and consider choosing an eco-friendly airline for your next flight. -->
                Pack light, as the weight of your luggage affects the plane's fuel efficiency.
              </li>
            </ol>

            <div class="more-content__ratings-container">
              <EButton
                class="more-contetnts__ratings"
                :icon="thumbUpIcon"
                :class="{'more-contetnts__ratings--active': activeRating === ratings.thumbUp}"
                variant="bare"
                icon-position="end"
                size="s"
                @click="setRating(ratings.thumbUp)"
              />
              <EButton
                class="more-contetnts__ratings"
                :icon="thumbDownIcon"
                :class="{'more-contetnts__ratings--active': activeRating === ratings.thumbDown }"
                variant="bare"
                icon-position="end"
                size="s"
                @click="setRating(ratings.thumbDown)"
              />
            </div>

            <div v-if="showMore" class="related-queries">
              <div>Related searches</div>
              <ELinkBubble
                v-for="(relatedQuery) in relatedQueries"
                :key="relatedQuery"
                as="a"
                :href="getUrl(relatedQuery)"
                target="_blank"
                class="related-query__link"
                size="l"
                color="result"
                :title="relatedQuery"
                at-breakpoint="desktop"
                @click="relatedQueryLinkClick"
              >
                <div class="related-queries__text-container">
                  <span
                    v-safe-html="relatedQuery"
                    class="related-queries__text"
                  />
                </div>
                <EIcon
                  aria-hidden="true"
                  icon="green-leaf"
                  size="m"
                  class="related-queries__icon"
                />
                <EIcon
                  aria-hidden="true"
                  icon="search"
                  size="s"
                  class="related-queries__icon"
                />
              </ELinkBubble>
            </div>
            <!-- <EGreenQueriesExplainer class="related-queries__green-explainer" /> -->
          </div>
        </transition>
      </div>
    </div>
    <div class="wave">
      <EWaveSidebar aria-hidden="true" />
    </div>
    <footer>
      <div class="footer-content-left">
        Powered by Ecosia and AI (Beta)
      </div>
      <div class="footer-content-right">
        <ELink as="button" color="secondary" @click="showFeedback">
          {{ $t('common.footer.feedback') }}
        </ELink>
      </div>
    </footer>
    <EFeedbackModal
      v-bind="feedbackModal"
      :active="isFeedbackModalVisible"
      @close="hideFeedbackModal"
    />
    <ERatingFeedbackModal
      v-if="isRatingModalvisible"
      :active="isRatingModalvisible"
      :rating="activeRating"
      @close="hideRatingModal"
      @rating-feedback-submitted="hideRatingModal"
    />
  </article>
</template>
<script>
import { mapGetters, mapState } from 'vuex';
import EBadge from '~~/common/vue/components/badge';
import EIcon from '~~/common/vue/components/icon';
import EHeading from '~~/common/vue/components/heading';
import EButton from '~~/common/vue/components/button';
import EFeedbackModal from '~~/common/vue/components/feedback-modal';
import ELink from '~~/common/vue/components/link';
import ELinkBubble from '~~/common/vue/components/link-bubble';
// import EGreenQueriesExplainer from '../related-queries/green-queries-explainer';
import ERatingFeedbackModal from './rating-feedback-modal';
import {
  greenGuideKeepSearchingClick,
  greenGuideHideMoreClick,
  greenGuideReadMoreClick,
  greenGuideDisplay,
  greenGuideRatingClick,
} from '~~/search/common/js/client/analytics/green-guide';
import { greenFeatures, greenGuides } from '~~/search/common/js/client/feature-flags';
import { getUrlQuery, baseUrlParams } from '~~/common/js/universal/search';

export default {
  name: 'GreenGuide',
  components: {
    EBadge,
    EIcon,
    EHeading,
    EButton,
    EWaveSidebar: async () => import('~~/search/common/vue/assets/img/waves-footer-sidebar.svg'),
    EFeedbackModal,
    ELink,
    ELinkBubble,
    // EGreenQueriesExplainer,
    ERatingFeedbackModal,
  },
  props: {
    guideType: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      showMore: false,
      isFeedbackModalVisible: false,
      isRatingModalvisible: false,
      activeRating: '',
    };
  },
  computed: {
    ...mapGetters('unleash', ['getFeatureToggle']),
    ...mapState('user', [
      'bingMarketCode',
      'defaultMarket',
    ]),
    isExperimentEnabled() {
      return (
        this.getFeatureToggle(greenFeatures.flag)?.value === greenFeatures.enabled &&
        this.getFeatureToggle(greenGuides.flag)?.value === greenGuides.enabled
      );
    },
    toggleIcon() {
      return this.showMore ? 'chevron-mini-up' : 'chevron-mini-down';
    },
    thumbUpIcon() {
      return 'thumb-up-plain';
    },
    thumbDownIcon() {
      return 'thumb-down-plain';
    },
    feedbackModal() {
      return {
        errorMessage: this.$t('common.header.feedback.error'),
        options: [
          { label: this.$t('common.header.feedback.bullet1'), value: 'helpful' },
          { label: this.$t('common.header.feedback.bullet2'), value: 'missing' },
          { label: this.$t('common.header.feedback.bullet3'), value: 'wrong' },
          { label: this.$t('common.header.feedback.bullet4'), value: 'not_helpful' },
        ],
        submitText: this.$t('common.header.feedback.send'),
        successMessage: this.$t('common.header.feedback.body2'),
        successTitle: this.$t('common.header.feedback.title2'),
        textareaPlaceholder: this.$t('common.header.feedback.tellmore'),
        title: this.$t('common.header.feedback.login'),
        ticketSubject: 'Feedback - Green - Green Guide',
        url: null,
        widgetType: 'green_guide',
        query: this.$route.query.q?.toString(),
        bingMarketCode: this.bingMarketCode || this.defaultMarket,
      };
    },
    relatedQueries() {
      return [
        'How to Choose an Eco-Friendly Airline',
        'Tips for Reducing Carbon Emissions while Flying',
        'How Carbon Offsets Work',
      ];
    },
    isGenericFlight() {
      return this.guideType === 'GENERIC_FLIGHT';
    },
    ratings() {
      return {
        thumbUp: 'thumb-up',
        thumbDown: 'thumb-down',
      };
    },
  },
  mounted() {
    this.$track(greenGuideDisplay);
  },
  methods: {
    toggleReadMore() {
      this.showMore = !this.showMore;
      if (this.showMore) {
        this.$track(greenGuideReadMoreClick);
      } else {
        this.$track(greenGuideHideMoreClick);
      }
    },
    hideFeedbackModal() {
      this.isFeedbackModalVisible = false;
    },
    hideRatingModal() {
      this.isRatingModalvisible = false;
    },
    showFeedback() {
      this.isFeedbackModalVisible = true;
    },
    relatedQueryLinkClick() {
      this.$track(greenGuideKeepSearchingClick);
      // this.$router.push({ path: this.getUrl(query) });
    },
    getUrl(q) {
      const query = {
        ...getUrlQuery(this.$route.query, this.$store.state.user, [
          ...baseUrlParams,
          'canary',
          'mocked',
          'forceProvider',
        ]),
        q,
      };
      return this.$router.resolve({ query }).href;
    },
    setRating(rating) {
      this.activeRating = rating;
      this.isRatingModalvisible = true;
      this.$track(greenGuideRatingClick(this.activeRating));
    },
  },
};
</script>

<style lang="scss" scoped>
.green-guide {
  position: relative;
  margin-top: $space-s;
  background-color: var(--color-background-secondary);
  border-radius: $space-s $space-s 0 0;
}

.content {
  padding: $space-m $space-m 0 $space-m;
  color: var(--color-text-primary);
}

.intro {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: $space-s;
  cursor: pointer;
}

.guide-text {
  margin-top: $space-m;
}

.sustainability-label {
  position: relative;
  font-weight: bold;
  overflow: hidden;
  height: $space-1l;
  transition: padding-right $timing-1s $easing;
  margin-bottom: $space-m;
}

.sustainability-label__icon {
  margin-left: -$space-2s;
  margin-right: $space-2s;
  z-index: 0;
}

.related-queries {
  opacity: 1;
  margin-top: $space-1l;
  margin-bottom: $space-l;
}

.related-queries > div {
  margin-bottom: $space-m;
}

.related-queries__text-container {
  min-width: 0;
  width: 100%;
}

.related-queries__text {
  @include line-clamp(2);

  min-width: 0;
  overflow-wrap: break-word;
}

.related-queries__icon {
  flex-shrink: 0;
  color: var(--color-decorative-icon);
  transition: color $timing-2s $easing;
  margin-left: $space-1s;
}

.related-query__link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0;
  border: 0;
  border-radius: 0;

  @include divider('bottom');
}

.wave {
  width: 100%;
  height: auto;
  color: var(--color-background-primary);
  overflow: hidden;
  position: relative;
}

.more-content {
  max-height: 800px;
  overflow: hidden;
}

.more-content__ratings-container {
  margin-top: $space-1l;
}

.more-contetnts__ratings {
  background-color: $white;

  &:hover,
  &--active {
    background-color: var(--color-button-background-transparent-hover);
  }
}

.green-guide ol {
  margin: 0;
  padding-left: $space-m;
}

.green-guide ol li {
  margin-bottom: $space-1s;
}

.toggle-enter-active,
.toggle-leave-active {
  transition: max-height $timing-m;
}

.toggle-enter,
.toggle-leave-to {
  max-height: 0;
}

footer {
  margin-top: -2px;
  background-color: var(--color-button-background-secondary);
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  font-size: $font-1s;
  color: var(--color-link-secondary);
  width: 100%;
  padding: 0 $space-m;
}

.dark {
  .sustainability-label {
    color: $c-gray-70;
  }
}

.read-more-btn-active {
  background: var(--color-button-background-transparent-hover);
}

.related-queries__green-explainer {
  margin-bottom: $space-1l;
}
</style>

<style lang="scss">
// within this label the green-leaf icons has reversed colors
// so we have to use the scss vars directly
.sustainability-label__icon {
  .green-leaf-bottom {
    fill: $c-green-30;
  }

  .green-leaf-top {
    fill: $c-green-20;
  }
}

.dark {
  .sustainability-label__icon {
    .green-leaf-bottom {
      fill: $c-green-70;
    }

    .green-leaf-top {
      fill: $c-green-50;
    }
  }
}
</style>
